/**
 * Pick → Verify → Ship ("Option 2") helpers — the client-side mirror of `companyUsesPickVerifyShip`
 * (@monrepo-build/db) and of the order resolution in libs/services pick-verify/workflow.ts. The
 * warehouse cannot import either (Docker build context), so the few rules it needs live here.
 *
 * FAIL-CLOSED TO OPTION 1 — the inverse of totes' fail-open default. Only the exact value
 * 'pick_verify_ship' means Option 2; `undefined` (a stale bundle, a selection that omits the field,
 * a non-vendor reader for whom the server returns null), `null`, 'standard' or anything else means
 * "every screen behaves exactly as it always has". A wrong guess in that direction costs a picker
 * one legible server refusal (the PICK_VERIFY_* gates cover every write); a wrong guess the other
 * way would send an Option-1 company's orders to screens it never asked for.
 *
 * WHICH READ DECIDES WHAT:
 *  - ROUTING, labels and the Summary buttons read the ORDER's `fulfillmentWorkflow` — the server's
 *    resolution, in which the order's own stamp wins over the company setting (a company switched
 *    back to Option 1 still finishes its started orders in Option 2). Always from the screen's own
 *    network-only document.
 *  - The list's TABS read the per-page `company(id:){ id fulfillmentWorkflow }`: a Verify tab only
 *    makes sense for a company whose new orders go through Verify.
 *  Never the root store: it is fetched once per app load and may be a whole shift stale.
 */

export const PICK_VERIFY_SHIP = 'pick_verify_ship';

/** Mirror of `STALE_PICK_CLAIM_MS` (15 min) in libs/db order.model — keep in step. */
export const STALE_PICK_CLAIM_MINUTES = 15;

export type PickVerifyStage = 'pick' | 'verify' | 'verified';

const PICK_VERIFY_STAGES: readonly string[] = ['pick', 'verify', 'verified'];

/** What the warehouse selects of `Order.fulfillmentWorkflow` (GraphQL `OrderFulfillmentWorkflow`). */
export type OrderFulfillmentWorkflowFields = {
  workflow?: string | null;
  stage?: string | null;
  started?: boolean | null;
  pickerUserId?: string | null;
  pickerName?: string | null;
  pickerClaimStale?: boolean | null;
};

export type PickVerifyOrderLike =
  | {
      id?: string | null;
      vendorStatus?: string | null;
      fulfillmentWorkflow?: OrderFulfillmentWorkflowFields | null;
    }
  | null
  | undefined;

export const companyUsesPickVerifyShip = (
  company: { fulfillmentWorkflow?: string | null } | null | undefined
): boolean => company?.fulfillmentWorkflow === PICK_VERIFY_SHIP;

/** Whether THIS order follows Option 2 (its stamp, or — not yet started — its company's setting). */
export const orderUsesPickVerifyShip = (order: PickVerifyOrderLike): boolean =>
  order?.fulfillmentWorkflow?.workflow === PICK_VERIFY_SHIP;

/**
 * The order's effective Option-2 stage, or null for a standard order (or a stage this bundle does
 * not know). The server already maps a verified order written back to `picking` to `verify`.
 */
export const pickVerifyStage = (order: PickVerifyOrderLike): PickVerifyStage | null => {
  if (!orderUsesPickVerifyShip(order)) return null;
  const stage = order?.fulfillmentWorkflow?.stage;
  return stage && PICK_VERIFY_STAGES.includes(stage) ? (stage as PickVerifyStage) : null;
};

export const salesOrderPickPath = (salesOrderId: string) => `/sales-order-pick/${salesOrderId}`;
export const salesOrderVerifyPath = (salesOrderId: string) =>
  `/sales-order-verify/${salesOrderId}`;

/** What an Option-2 order's next screen is for: Pick, Verify, or the verified order's documents. */
export type PickVerifyStep = 'pick' | 'verify' | 'documents';

/**
 * Which Option-2 screen an order belongs on, or null — "use the standard screens" — for every
 * standard order and for Option-2 orders that are past the new screens (shipping without a
 * `verified` stamp, shipped, completed, cancelled, on hold…).
 *
 * At `picking` every Option-2 order gets a screen: stage verify (or a defensive `verified`) →
 * Verify, anything else — pick, or a stage this bundle does not recognise — → Pick, which checks
 * the stage itself and forwards to Verify. Falling back to the standard Picking screen instead
 * would put an Option-2 order in front of writes the server refuses.
 */
export const pickVerifyStepFor = (order: PickVerifyOrderLike): PickVerifyStep | null => {
  if (!order?.id || !orderUsesPickVerifyShip(order)) return null;
  const stage = pickVerifyStage(order);
  if (order.vendorStatus === 'picking') {
    return stage === 'verify' || stage === 'verified' ? 'verify' : 'pick';
  }
  // Verified and waiting to ship: the Verify screen doubles as the pack-documents reprint.
  if (order.vendorStatus === 'shipping' && stage === 'verified') return 'documents';
  return null;
};

/**
 * `/sales-order-pick/:id` | `/sales-order-verify/:id` | null (see pickVerifyStepFor). Never
 * builds a path from a missing id — the router has no catch-all, so `/sales-order-pick/` would
 * render a blank page.
 */
export const pickVerifyRouteFor = (order: PickVerifyOrderLike): string | null => {
  const step = pickVerifyStepFor(order);
  if (!step || !order?.id) return null;
  return step === 'pick' ? salesOrderPickPath(order.id) : salesOrderVerifyPath(order.id);
};

export const pickVerifyStageLabel = (stage: string | null | undefined): string => {
  if (stage === 'pick') return 'Pick';
  if (stage === 'verify') return 'Verify';
  if (stage === 'verified') return 'Verified';
  return '';
};

/**
 * Who holds the Pick claim, for "Picking: <name>" — only while the order is in the Pick stage (the
 * claim covers Pick alone; Verify is open to anyone, and a split child keeps the old picker's
 * fields). A claim whose user no longer resolves to a name still reads as claimed.
 */
export const pickVerifyPickerName = (order: PickVerifyOrderLike): string | null => {
  if (pickVerifyStage(order) !== 'pick') return null;
  const { pickerName, pickerUserId } = order?.fulfillmentWorkflow || {};
  const name = typeof pickerName === 'string' ? pickerName.trim() : '';
  if (name) return name;
  return pickerUserId ? 'another user' : null;
};

/** The list tabs an Option-2 company sees. `picking` / `shipping` keep today's keys. */
export type PickVerifyListTab = 'picking' | 'verify' | 'shipping';

/** `?tab=` → tab, for an Option-2 company only (Option 1 keeps its own two-way parse). */
export const pickVerifyListTabFromParam = (param: string | null | undefined): PickVerifyListTab =>
  param === 'shipping' ? 'shipping' : param === 'verify' ? 'verify' : 'picking';

/**
 * Which tab an order sits under on an Option-2 company's list: Verify for an Option-2 order at
 * `picking` whose Pick is done, Pick for every other `picking` order (standard ones included — a
 * return, a grandfathered order), Ship for `shipping`. Null for anything else.
 */
export const pickVerifyListTab = (order: PickVerifyOrderLike): PickVerifyListTab | null => {
  if (order?.vendorStatus === 'shipping') return 'shipping';
  if (order?.vendorStatus !== 'picking') return null;
  const stage = pickVerifyStage(order);
  return stage === 'verify' || stage === 'verified' ? 'verify' : 'picking';
};

/**
 * The warehouse order list, open on `tab` — where an order screen's close (its circle-X) returns
 * to: Pick and Picking to Pick, Verify to Verify, Shipping and its documents to Ship. Pick is the
 * list's default, so it carries no `?tab=`; both workflows' lists read `?tab=shipping`, and only an
 * Option-2 company has a Verify screen to close.
 */
export const salesOrdersTabPath = (tab: PickVerifyListTab) =>
  tab === 'picking' ? '/sales-orders' : `/sales-orders?tab=${tab}`;

/** `/add-tote?returnTo=` value the Option-2 Pick screen passes so Add Tote returns to it. */
export const ADD_TOTE_RETURN_TO_PICK = 'pick';

export const addTotePathForPick = (salesOrderId: string) =>
  `/add-tote?salesOrderId=${salesOrderId}&returnTo=${ADD_TOTE_RETURN_TO_PICK}`;

/**
 * Where Add Tote goes back to: the Option-2 Pick screen when it was opened from there, otherwise —
 * any other or no `returnTo` — exactly the standard picking screen it has always returned to.
 */
export const addToteReturnPath = (
  salesOrderId: string,
  returnTo: string | null | undefined
): string =>
  returnTo === ADD_TOTE_RETURN_TO_PICK
    ? salesOrderPickPath(salesOrderId)
    : `/sales-order-picking/${salesOrderId}`;

const toMillis = (value: unknown): number => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value !== '') return new Date(value).getTime();
  return Number.NaN;
};

/**
 * Whole minutes from `dateLike` (a Date, an MDate ISO string or epoch ms) to `now`, floored. 0 for
 * a missing or unparseable value, and for a moment slightly in the future — a handheld's clock can
 * run ahead of the server's, and "-1 min ago" helps nobody.
 */
export const minutesSince = (dateLike: unknown, now: Date | number = new Date()): number => {
  const then = toMillis(dateLike);
  const nowMs = toMillis(now);
  if (Number.isNaN(then) || Number.isNaN(nowMs)) return 0;
  return Math.max(0, Math.floor((nowMs - then) / 60000));
};
